import React, { useState } from 'react';
import { 
  FaUserClock, 
  FaUserCheck, 
  FaUserTimes, 
  FaEye, 
  FaEyeSlash,
  FaClipboardList
} from 'react-icons/fa';

const SecureIntakeDrawer = ({ requests, onApprove, onReject }) => {
  const [selectedRequest, setSelectedRequest] = useState(requests[0] || null);
  const [revealedFields, setRevealedFields] = useState({});
  const [assignedRole, setAssignedRole] = useState('viewer');
  
  // Verification Checklist States
  const [checks, setChecks] = useState({
    identityVerified: false,
    emailConfirmed: true,
    consentSigned: false,
    healthIntakeDone: false
  });

  const allChecksPassed = Object.values(checks).every(Boolean);

  const handleSelectRequest = (req) => {
    setSelectedRequest(req);
    setRevealedFields({});
    setAssignedRole(req.role || 'viewer');
    setChecks({
      identityVerified: false,
      emailConfirmed: true,
      consentSigned: false,
      healthIntakeDone: false
    });
  };

  const toggleFieldReveal = (field) => {
    setRevealedFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getMaskedValue = (field, value) => {
    if (!value) return '—';
    if (revealedFields[field]) return value;
    if (field === 'phone') return '••• - ••• - ••••';
    if (field === 'dob') return '•••• / •• / ••';
    if (field === 'address') return '•••••••••••••••••••••';
    return '••••••••';
  };

  const handleApproveAction = () => {
    if (!selectedRequest || !allChecksPassed) return;
    
    // Trigger approval with updated role
    onApprove(selectedRequest.id);
    
    // Clear selections
    const remaining = requests.filter(r => r.id !== selectedRequest.id);
    setSelectedRequest(remaining[0] || null);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-8rem)]">
      {/* Left Registration Queue Feed */}
      <div className="w-full lg:w-80 glass-panel bg-white  rounded-xl border border-slate-200  flex flex-col p-4">
        <div className="flex items-center gap-2 border-b border-slate-300  pb-3 mb-3">
          <FaUserClock className="text-purple-600 " />
          <h3 className="font-bold text-slate-800  text-sm">Intake Queue ({requests.length})</h3>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {requests.length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-400 ">No pending registrations</div>
          ) : (
            requests.map(r => (
              <button
                key={r.id}
                onClick={() => handleSelectRequest(r)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                  selectedRequest?.id === r.id
                    ? 'border-purple-500 bg-purple-50/30 '
                    : 'border-slate-300  hover:bg-slate-50/50 :bg-slate-900/50'
                }`}
              >
                {r.avatar ? (
                  <img src={r.avatar} alt="avatar" className="w-9 h-9 rounded-full object-cover border border-slate-200 " />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-purple-100  text-purple-700  flex items-center justify-center font-bold text-xs">
                    {r.name.charAt(0)}
                  </div>
                )}
                <div className="truncate flex-1">
                  <h4 className="font-bold text-xs text-slate-800 ">{r.name}</h4>
                  <span className="text-xxs text-slate-400 ">{r.email}</span>
                </div>
                <span className="text-xxs px-2 py-0.5 rounded bg-amber-50  text-amber-700  border border-amber-200  font-medium">Pending</span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right Content Panel - Audit Detail Workspace */}
      {selectedRequest ? (
        <div className="flex-grow glass-panel bg-white  rounded-xl border border-slate-200  p-5 flex flex-col overflow-y-auto lg:flex-row gap-6">
          {/* Patient Details Pane */}
          <div className="flex-1 space-y-5">
            <div className="flex items-center gap-3 pb-3 border-b border-slate-300 ">
              <div className="h-12 w-12 rounded-full bg-slate-100  text-slate-700  flex items-center justify-center font-bold text-base">
                {selectedRequest.name.charAt(0)}
              </div>
              <div>
                <h2 className="font-extrabold text-slate-800  text-md">{selectedRequest.name}</h2>
                <p className="text-slate-400  text-xs">Submitted on: {selectedRequest.createdAt || '05/19/2026'}</p>
              </div>
            </div>

            {/* Demographics HIPAA Data Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="border border-slate-300  p-3 rounded-lg bg-slate-50/20 ">
                <span className="text-xxs text-slate-400  uppercase font-bold tracking-wider">Email Address</span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-semibold text-slate-700 ">{getMaskedValue('email', selectedRequest.email)}</span>
                  <button onClick={() => toggleFieldReveal('email')} className="text-slate-400  hover:text-purple-600 :text-purple-400 transition-colors">
                    {revealedFields.email ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                  </button>
                </div>
              </div>

              <div className="border border-slate-300  p-3 rounded-lg bg-slate-50/20 ">
                <span className="text-xxs text-slate-400  uppercase font-bold tracking-wider">Phone Contact</span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-semibold text-slate-700 ">{getMaskedValue('phone', selectedRequest.phone)}</span>
                  <button onClick={() => toggleFieldReveal('phone')} className="text-slate-400  hover:text-purple-600 :text-purple-400 transition-colors">
                    {revealedFields.phone ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                  </button>
                </div>
              </div>

              <div className="border border-slate-300  p-3 rounded-lg bg-slate-50/20 ">
                <span className="text-xxs text-slate-400  uppercase font-bold tracking-wider">Date of Birth</span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-semibold text-slate-700 ">{getMaskedValue('dob', selectedRequest.dob)}</span>
                  <button onClick={() => toggleFieldReveal('dob')} className="text-slate-400  hover:text-purple-600 :text-purple-400 transition-colors">
                    {revealedFields.dob ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                  </button>
                </div>
              </div>

              <div className="border border-slate-300  p-3 rounded-lg bg-slate-50/20 ">
                <span className="text-xxs text-slate-400  uppercase font-bold tracking-wider">Preferred Channel</span>
                <div className="font-semibold text-slate-700  mt-1 capitalize">{selectedRequest.preferredContact || 'Email'}</div>
              </div>

              <div className="border border-slate-300  p-3 rounded-lg bg-slate-50/20  md:col-span-2">
                <span className="text-xxs text-slate-400  uppercase font-bold tracking-wider">Residential Address</span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-semibold text-slate-700 ">{getMaskedValue('address', selectedRequest.address)}</span>
                  <button onClick={() => toggleFieldReveal('address')} className="text-slate-400  hover:text-purple-600 :text-purple-400 transition-colors">
                    {revealedFields.address ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Role assignment dropdown */}
            <div className="pt-2">
              <label className="block text-xxs font-bold text-slate-400  uppercase tracking-wider mb-2">Assign System Role</label>
              <select
                value={assignedRole}
                onChange={e => {
                  setAssignedRole(e.target.value);
                  selectedRequest.role = e.target.value;
                }}
                className="w-full sm:w-48 bg-white  border border-slate-200  rounded-lg p-2 text-xs focus:ring focus:ring-purple-200 text-slate-800  focus:outline-none capitalize font-medium"
              >
                <option value="viewer">Viewer / Guest</option>
                <option value="therapist">Therapist</option>
                <option value="receptionist">Receptionist</option>
                <option value="admin">System Admin</option>
              </select>
            </div>
          </div>

          {/* Audit Checklist & Action Block */}
          <div className="w-full lg:w-72 border-t lg:border-t-0 lg:border-l border-slate-300  pt-5 lg:pt-0 lg:pl-5 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex items-center gap-1.5 pb-2 border-b border-slate-300 ">
                <FaClipboardList className="text-purple-600 " />
                <h3 className="font-bold text-slate-800  text-xs">Verify Registration Tasks</h3>
              </div>

              {/* Checklist inputs */}
              <div className="space-y-3">
                <label className="flex items-start gap-2.5 text-xs text-slate-600  hover:text-slate-800 :text-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checks.identityVerified}
                    onChange={e => setChecks({ ...checks, identityVerified: e.target.checked })}
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 accent-purple-600 mt-0.5"
                  />
                  <div>
                    <div className="font-semibold">Identity Document Check</div>
                    <div className="text-slate-400  text-xxs">Verify name matches document file</div>
                  </div>
                </label>

                <label className="flex items-start gap-2.5 text-xs text-slate-600  hover:text-slate-800 :text-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checks.emailConfirmed}
                    onChange={e => setChecks({ ...checks, emailConfirmed: e.target.checked })}
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 accent-purple-600 mt-0.5"
                  />
                  <div>
                    <div className="font-semibold">Email Verification Pin</div>
                    <div className="text-slate-400  text-xxs">Confirm token matches record data</div>
                  </div>
                </label>

                <label className="flex items-start gap-2.5 text-xs text-slate-600  hover:text-slate-800 :text-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checks.consentSigned}
                    onChange={e => setChecks({ ...checks, consentSigned: e.target.checked })}
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 accent-purple-600 mt-0.5"
                  />
                  <div>
                    <div className="font-semibold">Consent Forms Signed</div>
                    <div className="text-slate-400  text-xxs">Waiver and telehealth documents verified</div>
                  </div>
                </label>

                <label className="flex items-start gap-2.5 text-xs text-slate-600  hover:text-slate-800 :text-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checks.healthIntakeDone}
                    onChange={e => setChecks({ ...checks, healthIntakeDone: e.target.checked })}
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 accent-purple-600 mt-0.5"
                  />
                  <div>
                    <div className="font-semibold">Health Intake Completed</div>
                    <div className="text-slate-400  text-xxs">Medical conditions and history uploaded</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Action buttons (Approve / Reject) */}
            <div className="space-y-2 mt-6">
              <button
                onClick={handleApproveAction}
                disabled={!allChecksPassed}
                className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-100 :bg-slate-900 disabled:text-gray-400 :text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2 rounded-lg text-xs font-bold transition-all"
                title={allChecksPassed ? "Approve patient into CRM" : "Complete checklist to approve"}
              >
                <FaUserCheck /> Approve Intake
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Reject registration request for ${selectedRequest.name}?`)) {
                    onReject(selectedRequest.id);
                    const remaining = requests.filter(r => r.id !== selectedRequest.id);
                    setSelectedRequest(remaining[0] || null);
                  }
                }}
                className="w-full flex items-center justify-center gap-2 border border-rose-200  bg-rose-50/50  hover:bg-rose-100 :bg-rose-950/40 text-rose-700  py-2 rounded-lg text-xs font-bold transition-all"
              >
                <FaUserTimes /> Reject request
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 glass-panel bg-white  rounded-xl border border-slate-200  grid place-items-center text-slate-400 ">
          No registration requests are currently pending in the intake queue
        </div>
      )}
    </div>
  );
};

export default SecureIntakeDrawer;
